import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import {
    BulkCustomAttributesRuntype,
    CustomAttributeDefinitionRuntype,
    CustomAttributeEntityTypeRuntype,
    EntityCustomAttributePatchRuntype,
    RegisterCustomAttributeDefinitionRuntype,
} from './customAttributesBackendTypes';

/**
 * Endpoints for the custom attributes store: string key-value pairs attached to
 * entities such as customers.
 *
 * A key has to be registered for an entity type before any value can be written
 * for it. Only organization users can call these endpoints; customer portal and
 * supplier users are rejected.
 */
export const customAttributesEndpoints = {
    'POST /custom-attribute-definitions/bulk': endpoint({
        description: 'Register custom attribute keys, at most 50 per entity type',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            definitions: z.array(RegisterCustomAttributeDefinitionRuntype),
        }),
        responseBody: z.array(CustomAttributeDefinitionRuntype),
        invalidates: ['GET /custom-attribute-definitions/:entityType'],
    }),

    'DELETE /custom-attribute-definitions/bulk': endpoint({
        description: 'Unregister custom attribute keys by id. Fails if a key still holds values',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            ids: z.array(z.string()),
        }),
        responseBody: z.object({
            deleted: z.number(),
        }),
        invalidates: ['GET /custom-attribute-definitions/:entityType'],
    }),

    'GET /custom-attribute-definitions/:entityType': endpoint({
        description: 'List the registered custom attribute keys of an entity type',
        pathParams: z.object({ entityType: CustomAttributeEntityTypeRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(CustomAttributeDefinitionRuntype),
    }),

    'POST /custom-attributes/:entityType/bulk': endpoint({
        description: 'Read the custom attributes of up to 1000 entities of one type',
        pathParams: z.object({ entityType: CustomAttributeEntityTypeRuntype }),
        queryParams: z.undefined(),
        requestBody: z.object({
            ids: z.array(z.string()),
        }),
        responseBody: BulkCustomAttributesRuntype,
    }),

    'PATCH /custom-attributes/:entityType/bulk': endpoint({
        description:
            'Set and remove custom attributes on up to 1000 entities of one type. Every key must be registered',
        pathParams: z.object({ entityType: CustomAttributeEntityTypeRuntype }),
        queryParams: z.undefined(),
        requestBody: z.object({
            patches: z.array(EntityCustomAttributePatchRuntype),
        }),
        responseBody: BulkCustomAttributesRuntype,
        invalidates: ['POST /custom-attributes/:entityType/bulk'],
    }),
};
